import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { useApp } from '@/presentation/hooks/use-app.js';

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: () => Promise<boolean>;
    testId: string;
}

export function DeleteConfirmDialog({ open, onClose, onConfirm, testId }: Props) {
    const { translate } = useApp();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        setError(false);
        try {
            const ok = await onConfirm();
            if (ok) {
                onClose();
            } else {
                setError(true);
            }
        } catch (err) {
            console.error('Delete operation failed', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setError(false);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth data-testid={testId}>
            <DialogTitle>{translate('common.confirmDelete')}</DialogTitle>
            <DialogContent>
                <Typography variant="body2">{translate('common.delete')}?</Typography>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {translate('common.deleteFailed')}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
                    {translate('common.cancel')}
                </Button>
                <Button color="error" variant="contained" onClick={handleConfirm} disabled={loading} sx={{ textTransform: 'none' }}>
                    {translate('common.delete')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
